<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import TagLabel from "./tag-label.vue";

  import { onKeyStroke } from "@vueuse/core";
  import { onMounted, nextTick } from "vue";
  import { isNil } from "lodash-es";

  const props = defineProps({
    id: { type: String, default: null },
    name: { type: String, default: null },
    color: { type: String, default: null },
    count: { type: Number, default: null },
  });

  const emit = defineEmits(["save", "cancel", "destroy"]);

  const colorInput = _ref(null);
  const dName = _ref(props.name);
  const dColor = _ref(props.color);

  let isConfirming = _ref(false);

  const serializedTag = _computed(() => {
    return {
      id: props.id,
      name: dName.value,
      color: dColor.value,
      count: props.count,
    };
  });

  const cancel = () => {
    emit("cancel", serializedTag.value);
  };

  const save = () => {
    emit("save", serializedTag.value);
  };

  const destroy = () => {
    emit("destroy", serializedTag.value);
    isConfirming.value = false;
  };

  const valid = _computed(() => {
    return dName.value && dName.value.trim().length > 0;
  });

  const computedName = _computed(() => {
    return dName.value.trim().length > 0 ? dName.value : "(tag name)";
  });

  onKeyStroke("Escape", cancel);
  onMounted(() => {
    nextTick(() => {
      colorInput.value.focus();
    });
  });
</script>

<template>
  <div class="modal is-active">
    <div class="modal-background" @click="cancel"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Editing Tag: {{ name }}</p>
        <button class="delete" aria-label="close" @click="cancel"></button>
      </header>
      <section class="modal-card-body">
        <p class="mb-2 has-text-centered">
          <span v-if="!isNil(props.count)">
            This tag applies to {{ props.count }} item{{ props.count === 1 ? "" : "s" }}.
          </span>
        </p>
        <div class="is-flex is-justify-content-center mt-4 mb-5">
          <TagLabel
            :name="computedName"
            :color="dColor"
            :can-click="false"
            :can-remove="false"
            size="large"
          />
        </div>
        <form @submit.prevent.stop="save">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">Name:</label>
            </div>
            <div class="field-body">
              <div class="control">
                <input ref="colorInput" v-model="dName" class="input" type="text" required />
              </div>
            </div>
          </div>

          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">Color:</label>
            </div>
            <div class="field-body">
              <div class="control">
                <input v-model="dColor" type="color" />
              </div>
            </div>
          </div>
        </form>
      </section>
      <footer class="modal-card-foot is-justify-content-space-between">
        <div class="is-flex">
          <form
            v-if="isConfirming"
            class="is-flex is-align-items-center gap-4"
            @submit.prevent="cancel"
          >
            <div>Are you sure?</div>
            <div class="is-flex">
              <button type="button" class="button is-small" @click="isConfirming = false">
                Cancel
              </button>
              <button type="button" class="button is-small is-danger" @click="destroy">
                Delete Tag
              </button>
            </div>
          </form>

          <form v-if="!isConfirming" @submit.prevent="isConfirming = true">
            <button type="submit" class="button">Delete Tag</button>
          </form>
        </div>

        <form @submit.prevent="save">
          <button :disabled="!valid" type="submit" class="button is-success">Save Tag</button>
        </form>
      </footer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .modal {
    user-select: none;
  }
</style>
